import { IcdOfficeModel, IcdCorporationStatusEnum } from "@herbjoy/apex-api-models";
import angular from "angular";
import { StateService, Transition } from "angular-ui-router";
import { configureStates, resolveFromParam, RouteDeclaration } from "../../core/router/router-helper.provider";
import { IcdCorporationService, IcdOfficeService, sortIcdOfficeByOwner } from "../../core/services";
import { UtilityService } from "../../core/services/utility.service";
import { MeridianIdMovementConfirmedModal } from "./move-meridian-id/meridian-id-movement-confirmed.component";
import { MeridianIdMovementSubmitted, MoveMeridianIdModal } from "./move-meridian-id/move-meridian-id-modal.component";

const states: RouteDeclaration[] = [{
    state: "icdCorporation",
    config: {
        name: "icdCorporation",
        abstract: true,
        url: "/icd-corporations"
    }
}, {
    state: "icdCorporation.list",
    config: {
        name: "icdCorporation.list",
        component: "apexListIcdCorporationContainer",
        url: "",
        data: {
            pageHeader: "ICD Corporations"
        }
    }
}, {
    state: "icdCorporation.new",
    config: {
        name: "icdCorporation.new",
        component: "apexEditIcdCorporation",
        url: "/new",
        data: {
            pageHeader: "New ICD Corporation"
        }
    }
}, {
    state: "icdCorporation.edit",
    config: {
        name: "icdCorporation.edit",
        component: "apexEditIcdCorporation",
        url: "/{icdCorporationID:int}/edit",
        resolve: {
            icdCorporationId: resolveFromParam("icdCorporationID")
        },
        data: {
            pageHeader: "Edit ICD Corporation"
        }
    }
}, {
    state: "icdCorporation.icdOffices",
    config: {
        name: "icdCorporation.icdOffices",
        url: "/{icdCorporationID:int}/icd-offices",
        abstract: true,
        resolve: {
            icdCorporationId: resolveFromParam("icdCorporationID")
        }
    }
}, {
    state: "icdCorporation.icdOffices.list",
    config: {
        name: "icdCorporation.icdOffices.list",
        component: "apexListIcdOfficesContainer",
        url: "",
        data: {
            pageHeader: "ICD Offices"
        }
    }
}, {
    state: "icdCorporation.icdOffices.new",
    config: {
        name: "icdCorporation.icdOffices.new",
        url: "/new",
        component: "apexEditIcdOffice",
        data: {
            pageHeader: "New ICD Office"
        }
    }
}, {
    state: "icdOffice",
    config: {
        name: "icdOffices",
        url: "/icd-offices",
        abstract: true
    }
}, {
    state: "icdOffice.edit",
    config: {
        name: "icdOffices.edit",
        url: "/{icdOfficeID:int}/edit?{icdCorporationID:int}",
        component: "apexEditIcdOffice",
        resolve: {
            icdOfficeId: resolveFromParam("icdOfficeID"),
            icdCorporationId: resolveFromParam("icdCorporationID")
        },
        data: {
            pageHeader: "Edit ICD Office"
        }
    }
}, {
    state: "icdOffice.edit.moveMeridianID",
    config: {
        name: "icdOffice.edit.moveMeridianID",
        url: "/move-meridian-id",
        resolve: {
            icdOffice: (icdOfficeId: number, icdOfficeService: IcdOfficeService) => {
                'ngInject';
                return icdOfficeService.retrieveIcdOffice(icdOfficeId);
            },
            icdCorporation: (icdOffice: IcdOfficeModel, icdCorporationService: IcdCorporationService) => {
                'ngInject';
                return icdCorporationService.retrieveIcdCorporation(icdOffice.IcdCorporationID);
            },
            targetIcdOffices: (icdOfficeId: number, icdOfficeService: IcdOfficeService) => {
                'ngInject';
                return icdOfficeService.retrieveIcdOffices({
                    icdCorporationStatus: IcdCorporationStatusEnum.Active
                })
                    .then(offices => offices.filter(p => p.Id !== icdOfficeId)
                        .sort(sortIcdOfficeByOwner));
            },
            onSubmit: ($state: StateService, icdOfficeId: number, icdOfficeService: IcdOfficeService,
                utilityService: UtilityService) => {
                'ngInject';
                return ({ icdOfficeID: targetIcdOfficeID }: MeridianIdMovementSubmitted) =>
                    utilityService.showLoadingUntilResolved(
                        utilityService.alertOnError(
                            icdOfficeService.moveMeridianOfficeID(icdOfficeId, { icdOfficeID: targetIcdOfficeID })
                                .then(() => {
                                    return $state.go("^.meridianIdMovementConfirmed", { targetIcdOfficeID });
                                }),
                            "Submitting Meridian ID movement failed"
                        ));
            }
        },
        onEnter: ["$state", "$uibModal", "icdOffice", "icdCorporation", "targetIcdOffices", "onSubmit",
            ($state: StateService, $uibModal, icdOffice, icdCorporation, targetIcdOffices, onSubmit) => {
                // Don't return this promise; UI router will wait on it, but it won't resolve until the modal has dismissed.
                $uibModal.open({
                    ...MoveMeridianIdModal,
                    resolve: {
                        icdOffice: () => icdOffice,
                        icdCorporation: () => icdCorporation,
                        targetIcdOffices: () => targetIcdOffices,
                        onSubmit: () => onSubmit
                    }
                }).result.catch(() => {
                    // Dismissing the modal should return to the parent office edit form state.
                    return $state.go("^", null, { supercede: false });
                })
            }],
        onExit: $uibModalStack => {
            // Handle back button events.
            'ngInject'
            $uibModalStack.dismissAll();
        }
    }
}, {
    state: "icdOffice.edit.meridianIdMovementConfirmed",
    config: {
        name: "icdOffice.edit.meridianIdMovementConfirmed",
        onEnter: ($state: StateService, $uibModal) => {
            'ngInject';
            $uibModal.open(MeridianIdMovementConfirmedModal).result
                .catch(() => {
                    // Dismissing the modal should return to the parent office edit form state.
                    return $state.go("^", null, { supercede: false });
                })
        },
        onExit: $uibModalStack => {
            // Handle back button events.
            'ngInject'
            $uibModalStack.dismissAll();
        }
    }
}];

const organizationRedirectStates: RouteDeclaration[] = [{
    state: "organization",
    config: {
        name: "organization",
        abstract: true,
        url: "/organization"
    }
}, {
    state: "organization.listIcdCorporations",
    config: {
        name: "organization.listIcdCorporations",
        url: "/listicdcorporations",
        redirectTo: "icdCorporation.list"
    }
}, {
    state: "organization.editIcdCorporation",
    config: {
        name: "organization.editIcdCorporation",
        url: "/editicdcorporation/{icdCorporationID:int}",
        redirectTo: (transition: Transition) => transition.params().icdCorporationID
            ? "icdCorporation.edit" : "icdCorporation.new",
        params: {
            icdCorporationID: { squash: true, value: null }
        }
    }
}, {
    state: "organization.listIcdOffices",
    config: {
        name: "organization.listIcdOffices",
        url: "/listicdoffices?{icdCorporationID:int}",
        redirectTo: "icdCorporation.icdOffices"
    }
}, {
    state: "organization.editIcdOffice",
    config: {
        name: "organization.editIcdOffice",
        url: "/editicdoffice/{icdOfficeID:int}?{icdCorporationID:int}",
        redirectTo: (transition: Transition) => transition.params().icdOfficeID
            ? "icdOffice.edit" : "icdCorporation.icdOffices.new",
        params: {
            icdOfficeID: { squash: true, value: null }
        }
    }
}, {
    state: "organization.teleMapperOfficePreview",
    config: {
        name: "organization.teleMapperOfficePreview",
        url: "/telemapperofficepreview/{meridianOfficeID:int}",
        params: {
            requireReload: true
        },
        data: {
            isMvc: true,
            pageHeader: "TeleMapper Office Preview"
        }
    }
}];

export const IcdCorporationRoutingModule = angular
    .module("apex.mainApp.icdCorporation.routing", [])
    .run(configureStates(states))
    .run(configureStates(organizationRedirectStates))
    .name;
